import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3000';
  return `${protocol}//${hostname}`;
};

const Home = () => {
  const { user, loading } = useAuth();
  const [teaser, setTeaser] = useState(null);
  const [aiTop, setAiTop] = useState(null);

  useEffect(() => {
    // Only fetch the public teaser for anonymous visitors (logged-in users have
    // the full Stock Picks page).
    if (user) return;
    fetch(`${getApiUrl()}/api/public/top-pick-teaser?cache=${Date.now()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && !d.error) setTeaser(d); })
      .catch(() => {});
    // Top AI-rated names of the month (teaser -> full /ai-rank page requires login).
    fetch(`${getApiUrl()}/api/public/ai-rank-top?limit=5&cache=${Date.now()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && Array.isArray(d.rows)) setAiTop(d); })
      .catch(() => {});
  }, [user]);

  // Home is a marketing/landing page. Once authenticated, AI Stock Picks is the
  // user's home — send them there (also what the logo click resolves to).
  // Guard on !loading so anonymous visitors aren't bounced during the auth check.
  if (!loading && user) {
    return <Navigate to="/strategy" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Hero Section */}
      <div className="container-custom py-20">
        <div className="text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            AI-Identified Stocks With
            <span className="text-gradient block mt-2">
              6–12 Month Upside Potential
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Our internal AI analyzes thousands of signals to identify high-potential stocks before they break out.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <Link to="/strategy" className="btn-primary btn-lg">
                View AI Stock Picks
              </Link>
            ) : (
              <>
                <Link to="/sample-pick" className="btn-primary btn-lg">
                  View Sample Pick
                </Link>
                <Link to="/register" className="btn-outline btn-lg">
                  Create Free Account
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Social-proof / results teaser (anonymous visitors only) */}
      {!user && teaser && (teaser.recentResults?.length > 0 || teaser.currentPick?.live) && (
        <div className="container-custom pb-10">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Locked current pick */}
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">This Month's Top Pick Is Live</h3>
                {teaser.currentPick?.live ? (
                  <>
                    <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                      <span className="inline-flex items-center gap-2 bg-gray-100 border border-gray-300 rounded-lg px-4 py-2">
                        <span className="text-2xl">🔒</span>
                        <span className="text-lg font-bold tracking-widest text-gray-400 select-none">• • • •</span>
                      </span>
                      {typeof teaser.currentPick.returnPct === 'number' && (
                        <span className={`text-xl font-bold ${teaser.currentPick.returnPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {teaser.currentPick.returnPct >= 0 ? '+' : ''}{teaser.currentPick.returnPct}%
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-4">
                      Our highest-conviction pick is live right now. Create a free account to unlock the symbol.
                    </p>
                  </>
                ) : (
                  <p className="text-gray-600 mb-4">
                    New picks drop regularly. Create a free account to get them as they go live.
                  </p>
                )}
                <Link to="/register" className="btn-primary">Unlock the Pick — Free</Link>
              </div>

              {/* Recent results */}
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <h4 className="text-lg font-semibold text-gray-900">Recent Picks</h4>
                  {typeof teaser.winRatePct === 'number' && (
                    <span className="text-sm text-gray-500">
                      {teaser.winRatePct}% win rate{teaser.sampleSize ? ` · ${teaser.sampleSize} trades` : ''}
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  {teaser.recentResults.slice(0, 6).map((r, i) => (
                    <div key={i} className="flex justify-between items-center py-1.5 border-b border-gray-100 text-sm">
                      <span className="font-semibold text-gray-900">{r.symbol}</span>
                      <span className="text-gray-400">{r.entry_date} → {r.exit_date}</span>
                      <span className={`font-bold ${Number(r.return_pct) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {Number(r.return_pct) >= 0 ? '+' : ''}{r.return_pct}%
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-3">
                  Past performance does not guarantee future results. Informational only — not investment advice.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top AI-rated stocks teaser (anonymous visitors) -> full /ai-rank requires login */}
      {!user && aiTop && aiTop.rows && aiTop.rows.length > 0 && (
        <div className="container-custom pb-10">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-5xl mx-auto">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Top AI-Rated Stocks</h3>
                <p className="text-gray-500 text-sm mt-1">
                  Our AI scored {aiTop.total} quality stocks this month. Here are the highest-conviction picks.
                </p>
              </div>
            </div>
            <div className="space-y-2 mb-5">
              {aiTop.rows.map((r, i) => (
                <div key={i} className="py-2 border-b border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-gray-900 w-20">{r.symbol}</span>
                    <span className="text-gray-500 flex-1 truncate px-2 hidden sm:block">{r.company_name || ''}</span>
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 mr-3">
                      {r.verdict}
                    </span>
                    <span className="text-gray-700 font-medium w-14 text-right">
                      {r.confidence != null ? `${Number(r.confidence).toFixed(0)}%` : ''}
                    </span>
                  </div>
                  {r.rationale_snippet && (
                    <p className="text-xs text-gray-500 mt-1 leading-snug">{r.rationale_snippet}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center">
              <Link to="/register" className="btn-primary">See the full AI ranking — Free</Link>
              <p className="text-[11px] text-gray-400 mt-3">
                AI-generated, advisory only — not investment advice. Full rankings require a free account.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Features Section */}
      <div className="bg-white py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Everything You Need for Smart Trading
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive tools and data to make informed investment decisions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link
              to="/strategy"
              className="text-center p-6 rounded-lg transition-shadow hover:shadow-md block"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">AI Stock Picks</h3>
              <p className="text-gray-600">
                Our AI's current high-conviction stock picks, updated as new signals fire
              </p>
            </Link>

            <Link
              to={user ? '/stock/stock-analyze' : '/register'}
              className="text-center p-6 rounded-lg transition-shadow hover:shadow-md block"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Advanced Analytics</h3>
              <p className="text-gray-600">
                Technical indicators, charts, and professional-grade analysis tools
              </p>
            </Link>

            <Link
              to={user ? '/billing' : '/pricing'}
              className="text-center p-6 rounded-lg transition-shadow hover:shadow-md block"
            >
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Flexible Plans</h3>
              <p className="text-gray-600">
                Choose from multiple subscription tiers to match your trading style
              </p>
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-600 py-16">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Trading Smarter?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of traders who trust our platform for their investment decisions
          </p>
          {!user && (
            <Link to="/register" className="btn bg-white text-blue-600 hover:bg-gray-100 btn-lg">
              Start Your Free Trial
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
